/* Android PWA install-prompt hook.
 *
 * Listens for `beforeinstallprompt` (fired by Chrome / Edge / Samsung Internet
 * on Android when the site meets install criteria) and exposes a simple
 * React-friendly API:
 *
 *   const { canInstall, isStandalone, isIOS, promptInstall, installed } = useInstallPrompt();
 *
 * - canInstall  : true while the captured event is still valid (single-use)
 * - isStandalone: the app is already running as an installed PWA
 * - isIOS       : iOS Safari (no beforeinstallprompt — use our text guide)
 * - promptInstall(): call on user gesture to show the native install dialog
 * - installed   : set once the user actually installs (via `appinstalled`)
 *
 * Intentionally tiny and side-effect-free outside React hooks.
 */
import { useCallback, useEffect, useState } from 'react';

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

function checkStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS
  const ios = (window.navigator as any).standalone === true;
  // Android / desktop Chrome
  const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  return Boolean(ios || mq);
}

function checkIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(false);
  const [standalone, setStandalone] = useState<boolean>(checkStandalone());

  useEffect(() => {
    const onBIP = (e: Event) => {
      // Prevent the automatic mini-info bar on some Android versions
      e.preventDefault();
      setDeferred(e as BIPEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    const mq = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
    const onMqChange = () => setStandalone(checkStandalone());

    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    mq && mq.addEventListener && mq.addEventListener('change', onMqChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      mq && mq.removeEventListener && mq.removeEventListener('change', onMqChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: 'unavailable' as const };
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      // The event can only be used once; clear it regardless of outcome.
      setDeferred(null);
      return { outcome: choice.outcome };
    } catch {
      setDeferred(null);
      return { outcome: 'dismissed' as const };
    }
  }, [deferred]);

  return {
    canInstall: Boolean(deferred) && !standalone && !installed,
    isStandalone: standalone,
    isIOS: checkIOS(),
    promptInstall,
    installed,
  };
}
